import React, { useState, useRef, useMemo, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

const NewDropdown = ({
   label = "Select Option",
   options = [],
   placeholder = "Search...",
   isMulti = false,
   darkMode = false,
   isLoading = false,
   value,
   onChange,
   onAddNew,
}) => {
   const [searchTerm, setSearchTerm] = useState("");
   const [isOpen, setIsOpen] = useState(false);
   const [focusedIndex, setFocusedIndex] = useState(-1);
   const parentRef = useRef(null);
   const inputRef = useRef(null);

   const getThemeClass = (light, dark) => (darkMode ? dark : light);

   const mainOptions = options.map((option) => ({
      label: option.name,
      value: option.id,
   }));

   const filteredOptions = useMemo(() => {
      if (!searchTerm) return mainOptions;
      return mainOptions.filter((opt) =>
         opt.label?.toLowerCase().includes(searchTerm.toLowerCase())
      );
   }, [mainOptions, searchTerm]);

   const rowVirtualizer = useVirtualizer({
      count: filteredOptions.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => 40,
      overscan: 5,
      measureElement: (el) => el.getBoundingClientRect().height,
   });

   const handleSelect = (option) => {
      let newValue;
      if (isMulti) {
         const current = Array.isArray(value) ? [...value] : [];
         const exists = current.includes(option.value);
         newValue = exists
            ? current.filter((v) => v !== option.value)
            : [...current, option.value];
      } else {
         newValue = option.value;
         setIsOpen(false);
      }
      setSearchTerm("");
      onChange?.(newValue);
   };

   const isSelected = (option) =>
      isMulti
         ? Array.isArray(value) && value.includes(option.value)
         : value === option.value;

   // 🔽 Handle keyboard navigation
   const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === "ArrowDown") {
         e.preventDefault();
         setFocusedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
         );
      } else if (e.key === "ArrowUp") {
         e.preventDefault();
         setFocusedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1
         );
      } else if (e.key === "Enter") {
         e.preventDefault();
         if (filteredOptions[focusedIndex]) {
            handleSelect(filteredOptions[focusedIndex]);
         }
      } else if (e.key === "Escape") {
         e.preventDefault();
         setIsOpen(false);
      }
   };

   // 🧭 Move scroll with keyboard arrows
   useEffect(() => {
      if (focusedIndex >= 0) {
         rowVirtualizer.scrollToIndex(focusedIndex, { align: "auto" });
      }
   }, [focusedIndex, rowVirtualizer]);

   // 🧹 Clear search when dropdown closes
   useEffect(() => {
      if (!isOpen) {
         setSearchTerm("");
         setFocusedIndex(-1);
      }
   }, [isOpen]);

   // 🧭 Scroll to selected when dropdown opens (only once)
   useEffect(() => {
      if (isOpen && !isMulti && value) {
         const selectedIndex = filteredOptions.findIndex((o) => o.value === value);
         if (selectedIndex >= 0) {
            setTimeout(() => {
               rowVirtualizer.scrollToIndex(selectedIndex, { align: "center" });
               setFocusedIndex(selectedIndex);
            }, 50);
         }
      }
   }, [isOpen]);

   const dropdownRef = useRef(null);

   useEffect(() => {
      const handleClickOutside = (event) => {
         if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setIsOpen(false);
         }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
         document.removeEventListener("mousedown", handleClickOutside);
      };
   }, []);

   return (
      <div ref={dropdownRef} className="w-full relative" onKeyDown={handleKeyDown}>
         {label && (
            <label className={`block mb-1 text-sm font-medium ${getThemeClass("text-gray-700", "text-white")}`}>
               {label}
            </label>
         )}

         <button
            type="button"
            onClick={() => {
               setIsOpen((p) => !p);
               setTimeout(() => inputRef.current?.focus(), 50);
            }}
            className={`flex justify-between items-center border rounded-lg px-3 py-2 text-left 
               ${isOpen ? "border-blue-500 ring-1 ring-blue-300" : ""}
               ${getThemeClass(
               "bg-white text-gray-800 border-gray-300 hover:border-gray-400",
               "bg-gray-800 text-white border-gray-600 hover:border-gray-500"
            )}
               w-[300px]
               max-w-full
            `}
         >
            <span
               className="block truncate flex-1"
               title={
                  !isMulti
                     ? mainOptions.find((o) => o.value === value)?.label
                     : `${value?.length ?? 0} selected`
               }
            >
               {isMulti
                  ? (value?.length ?? 0) > 0
                     ? `${value.length} selected`
                     : placeholder
                  : mainOptions.find((o) => o.value === value)?.label || placeholder}
            </span>
            {((!isMulti && value) || (isMulti && value?.length > 0)) && (
               <svg
                  onClick={(e) => {
                     e.stopPropagation(); // prevent dropdown toggle
                     onChange?.(isMulti ? [] : null); // clear selection
                  }}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-4 h-4 ml-2 text-gray-400 hover:text-red-500 cursor-pointer flex-shrink-0"
               >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
               </svg>
            )}

            <svg
               className={`w-4 h-4 ml-2 transition-transform ${isOpen ? "rotate-180" : ""
                  }`}
               fill="none"
               stroke="currentColor"
               viewBox="0 0 24 24"
            >
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
               />
            </svg>
         </button>

         {isOpen && (
            <div
               className={`absolute z-50 w-full mt-1 border rounded-lg shadow-lg overflow-hidden ${getThemeClass(
                  "bg-white border-gray-200",
                  "bg-gray-800 border-gray-700"
               )}`}
            >
               {/* Search */}
               <div className="p-2 border-b border-gray-200">
                  <input
                     ref={inputRef}
                     type="text"
                     placeholder="Search..."
                     className={`w-full px-3 py-2 border rounded-md text-sm outline-none ${getThemeClass(
                        "border-gray-300 bg-white text-gray-900 placeholder-gray-500",
                        "border-gray-600 bg-gray-700 text-white placeholder-gray-400"
                     )}`}
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                  />
               </div>

               {/* List */}
               <div
                  ref={parentRef}
                  className="max-h-60 overflow-auto"
                  style={{ position: "relative" }}
               >
                  {isLoading ? (
                     <div className="flex items-center justify-center py-4">
                        <div
                           className={`animate-spin rounded-full h-6 w-6 border-b-2 ${getThemeClass(
                              "border-blue-600",
                              "border-blue-400"
                           )}`}
                        ></div>
                     </div>
                  ) : filteredOptions.length === 0 ? (
                     <div className="text-center py-4">
                        <p className="text-gray-500 text-sm mb-2">No data found</p>
                        {onAddNew && searchTerm && (
                           <button
                              onClick={() => onAddNew(searchTerm)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                           >
                              + Add “{searchTerm}”
                           </button>
                        )}
                     </div>
                  ) : (
                     <div
                        style={{
                           height: `${rowVirtualizer.getTotalSize()}px`,
                           position: "relative",
                        }}
                     >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                           const option = filteredOptions[virtualRow.index];
                           const isFocused = focusedIndex === virtualRow.index;

                           return (
                              <div
                                 key={option.value}
                                 className={`absolute left-0 right-0 flex items-center px-3 py-2 cursor-pointer border-b ${isFocused
                                    ? getThemeClass(
                                       "bg-blue-100 border-blue-300",
                                       "bg-blue-700 border-blue-500"
                                    )
                                    : getThemeClass(
                                       isSelected(option)
                                          ? "bg-blue-50 border-blue-200"
                                          : "hover:bg-gray-100 border-gray-100",
                                       isSelected(option)
                                          ? "bg-blue-900 border-blue-700"
                                          : "hover:bg-gray-700 border-gray-700"
                                    )
                                    }`}
                                 style={{
                                    transform: `translateY(${virtualRow.start}px)`,
                                 }}
                                 onClick={() => handleSelect(option)}
                              >
                                 {isMulti && (
                                    <input
                                       type="checkbox"
                                       checked={isSelected(option)}
                                       readOnly
                                       className="mr-2 accent-blue-600"
                                    />
                                 )}
                                 <span className="truncate block w-full">{option.label}</span>
                              </div>
                           );
                        })}
                     </div>
                  )}
               </div>
            </div>
         )}
      </div>
   );
};

export default NewDropdown;
